import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { billingsService } from 'services/billingService'
import { sortByDate, standarizeTaskObj } from 'utils/helpers'

const initialState = {
  billings: {
    data: [],
    pagination: { total: 0, currentPage: 1, nextPage: null, prevPage: null },
  },
  currentBilling: null,
  userTasks: [],
  loading: false,
  error: null,
}

export const adminGetBillings = createAsyncThunk('list-billings', async (params = {}, { rejectWithValue }) => {
  try {
    const response = await billingsService.adminGetBillings(params)
    return response.data
  } catch (error) {
    if (error.response && error.response.data.message) return rejectWithValue(error.response.data.message)
    return rejectWithValue(error.message)
  }
})

export const adminGetBillingDetails = createAsyncThunk('billing-details', async (billingId, { rejectWithValue }) => {
  try {
    const response = await billingsService.adminGetBillingDetails(billingId)
    return response.data
  } catch (error) {
    if (error.response && error.response.data.message) return rejectWithValue(error.response.data.message)
    return rejectWithValue(error.message)
  }
})

export const adminGetUserAvailableTasks = createAsyncThunk('list-user-tasks', async (userId, { rejectWithValue }) => {
  try {
    const response = await billingsService.adminGetUserAvailableTasks(userId)
    return response.data
  } catch (error) {
    if (error.response && error.response.data.message) return rejectWithValue(error.response.data.message)
    return rejectWithValue(error.message)
  }
})

export const userGetBillings = createAsyncThunk('list-billings-user', async (params = {}, { rejectWithValue }) => {
  try {
    const response = await billingsService.userGetBillings(params)
    return response.data
  } catch (error) {
    if (error.response && error.response.data.message) return rejectWithValue(error.response.data.message)
    return rejectWithValue(error.message)
  }
})

export const userGetBillingDetails = createAsyncThunk(
  'billing-details-user',
  async (billingId, { rejectWithValue }) => {
    try {
      const response = await billingsService.userGetBillingDetails(billingId)
      return response.data
    } catch (error) {
      if (error.response && error.response.data.message) return rejectWithValue(error.response.data.message)
      return rejectWithValue(error.message)
    }
  }
)

export const billingsSlice = createSlice({
  name: 'billingsV2',
  initialState,
  reducers: {
    clean: () => {
      return initialState
    },
    cleanupRequestStatus: (state) => {
      state.error = false
      state.loading = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Get billings for admin
      .addCase(adminGetBillings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(adminGetBillings.fulfilled, (state, { payload }) => {
        state.loading = false
        state.billings = payload
      })
      .addCase(adminGetBillings.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })
      // Get billing details for admin
      .addCase(adminGetBillingDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(adminGetBillingDetails.fulfilled, (state, { payload }) => {
        state.loading = false
        state.currentBilling = payload
        const sortedTasks = sortByDate(payload.tasks)
        state.currentBilling.tasks = sortedTasks.map(standarizeTaskObj)
      })
      .addCase(adminGetBillingDetails.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })
      // Get user available tasks list for admin
      .addCase(adminGetUserAvailableTasks.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(adminGetUserAvailableTasks.fulfilled, (state, { payload }) => {
        state.loading = false
        const sortedTasks = sortByDate(payload)
        state.userTasks = sortedTasks.map(standarizeTaskObj)
      })
      .addCase(adminGetUserAvailableTasks.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })
      // Get billings for users
      .addCase(userGetBillings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(userGetBillings.fulfilled, (state, { payload }) => {
        state.loading = false
        state.billings.data = payload
      })
      .addCase(userGetBillings.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })
      // Get billing details for users
      .addCase(userGetBillingDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(userGetBillingDetails.fulfilled, (state, { payload }) => {
        state.loading = false
        state.currentBilling = payload
        const sortedTasks = sortByDate(payload.tasks)
        state.currentBilling.tasks = sortedTasks.map(standarizeTaskObj)
      })
      .addCase(userGetBillingDetails.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })
  },
})

export const { clean, cleanupRequestStatus } = billingsSlice.actions

export default billingsSlice.reducer
